import React, { useState } from 'react';
import { gradeColors } from './Constants';
import RatingsTable from './RatingsTable';
import { safeParse, isFailed, isRunning } from './utils';

/**
 * Helper function to format numbers consistently
 */
const formatRating = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  
  const num = parseFloat(value);
  if (!Number.isFinite(num)) {
    return '—';
  }
  
  // Format to 1 decimal place, then remove trailing .0
  return num.toFixed(1).replace(/\.0$/, '');
};

/**
 * Component for rendering changes in sidebar layout
 */
const ChangesDisplay = ({ changes }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Handle cases where changes might be empty
  if (!changes || changes.length === 0) {
    return <div className="alert alert-info">No changes to display.</div>;
  }

  const selectedItem = changes[selectedIndex];

  return (
    <div>
      {/* (New) Added a title and instructional text for clarity */}
      <p className="text-muted small mb-3">
        Click on a change in the left panel to view its details on the right.
      </p>

      {/* Changes Layout */}
      <div className="row">
        {/* Left Panel - Clickable Changes List */}
        <div className="col-md-5">
          <div className="list-group">
            {changes.map((item, i) => (
              <div
                key={i}
                // (Changed) Added classes for flex layout and a custom hover class
                className={`list-group-item list-group-item-action interactive-list-item d-flex justify-content-between align-items-center ${
                  i === selectedIndex ? 'active' : ''
                }`}
                onClick={() => setSelectedIndex(i)}
                style={{ cursor: 'pointer' }}
              >
                {/* Wrapped content in a div to group it */}
                <div>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {Array.isArray(item.theory.tags) ? item.theory.tags.map((tag, j) => (
                      <span key={j} className="badge bg-secondary small">{String(tag)}</span>
                    )) : (
                      <span className="badge bg-secondary small">{String(item.theory.tags)}</span>
                    )}
                  </div>
                  <div className="small fw-bold">{String(item.change)}</div>
                </div>
                {/* (New) Add a visual cue (chevron icon) to inactive items */}
                {i !== selectedIndex && <span className="text-muted h5 mb-0">&raquo;</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Details of the Selected Change */}
        <div className="col-md-7">
          <div className="border rounded p-3 h-100">
            <h5 className="mb-3">{String(selectedItem.change)}</h5>
            
            <div className="mb-4">
              <h6 className="text-success mb-2">Why It Helps:</h6>
              <p className="small text-muted mb-0">{String(selectedItem.why_it_helps)}</p>
            </div>
            
            <div className="mb-4">
              <h6 className="text-info mb-2">Theory & Explanation:</h6>
              <p className="small text-muted mb-0">{String(selectedItem.theory.explanation)}</p>
            </div>
            
            {selectedItem.theory.example_scenes && (
              <div>
                <h6 className="text-warning mb-2">Example Scenes:</h6>
                <div className="small text-muted">
                  {Array.isArray(selectedItem.theory.example_scenes) ? (
                    <ul className="mb-0 ps-3">
                      {selectedItem.theory.example_scenes.map((scene, k) => (
                        <li key={k}>{String(scene)}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-0">{String(selectedItem.theory.example_scenes)}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Rating grid + "view draft" modals
 *
 * Props
 * gens            Array of all versions to display (including original)
 * originalRatings ratings of the untouched scene (for modals)
 */
export default function GeneratedDraftsPanel({ gens, originalRatings }) {
  /* ─────────────────────────────────────────────────────── rendering helpers */
  const ratingLabels = {
    rating: 'Overall',
    conceptrating: 'Concept',
    plotrating: 'Plot',
    charactersrating: 'Characters',
    emotional_impact: 'Emotional Impact',
    conflict_level: 'Conflict Level',
    dialoguerating: 'Dialogue',
    storyforward: 'Story Forward',
    characterchanges: 'Character Changes',
    highstakes: 'High Stakes',
    internal_goal_score: 'Internal Goal',
    external_goal_score: 'External Goal',
    originality_score: 'Originality',
    engagement_score: 'Engagement',
    pacing_score: 'Pacing',
    formatting_score: 'Formatting',
    structure_score: 'Structure',
  };
  const ratingKeys = Object.keys(ratingLabels);

  // best value in each column (so we can draw the purple ring)
  const best = {};
  ratingKeys.forEach(k => {
    best[k] = Math.max(
      ...gens.map(g => {
        const ratings =
          typeof g.ratings === 'string'
            ? safeParse(g.ratings)
            : g.ratings || {};
        const num = parseFloat(ratings?.[k]);
        return Number.isFinite(num) ? num : -Infinity;
      }),
    );
  });

  /* ──────────────────────────────────────────────────────────────── render */
  return (
    <div className="table-responsive mb-3">
      <table className="table table-striped table-sm align-middle">
        <thead>
          <tr>
            <th style={{ width: 150 }}>Version / Date</th>
            {ratingKeys.map(k => (
              <th key={k}>{ratingLabels[k]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {gens.map(g => {
            const ratings =
              typeof g.ratings === 'string'
                ? safeParse(g.ratings)
                : g.ratings || {};
            return (
              <tr
                key={g.uuid}
                /* Three states, not two: red for failed, amber for still running,
                   nothing for done. Amber on a dead row read as "nearly there". */
                className={
                  isFailed(g.status) ? 'table-danger'
                    : isRunning(g.status) ? 'table-warning'
                      : ''
                }
              >
                {/* version / date cell */}
                <td className="text-nowrap">
                  {g.isOriginal ? (
                    'Original'
                  ) : (
                    <button
                      className="btn btn-link p-0"
                      data-bs-toggle="modal"
                      data-bs-target={`#genModal${g.uuid}`}
                    >
                      {new Date(g.created_at).toLocaleString()}
                    </button>
                  )}
                </td>
                {/* ratings cells */}
                {ratingKeys.map(k => {
                  const rawValue = ratings[k];
                  const formattedValue = formatRating(rawValue);
                  const numValue = parseFloat(rawValue);
                  const isValidNumber = Number.isFinite(numValue);
                  const isBest = isValidNumber && numValue === best[k];
                  const bg = isValidNumber ? gradeColors[Math.round(numValue)] : 'transparent';

                  return (
                    <td
                      key={k}
                      style={{ backgroundColor: bg, textAlign: 'center' }}
                    >
                      {isBest ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '50%',
                            border: '2px solid purple',
                            fontWeight: 'bold',
                          }}
                        >
                          {formattedValue}
                        </span>
                      ) : (
                        formattedValue
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* one modal per generated draft (skip the original row) */}
      {gens
        .filter(g => !g.isOriginal)
        .map(g => (
          <div
            key={g.uuid}
            className="modal fade"
            id={`genModal${g.uuid}`}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Draft {new Date(g.created_at).toLocaleString()}
                  </h5>
                  <button className="btn-close" data-bs-dismiss="modal" />
                </div>
                <div className="modal-body">
                  <h6>Generated Scene</h6>
                  <pre className="p-3 border w-100" style={{whiteSpace:'pre-wrap', wordBreak:'break-word', overflowX:'auto'}}>
                    {g.scene || '(still generating…)'}
                  </pre>
                  {/* Comments Section */}
                  {(() => {
                    const c = safeParse(g.comments);
                    
                    if (!c || (Array.isArray(c) && c.length === 0) || (typeof c === 'object' && Object.keys(c).length === 0)) {
                      return null;
                    }

                    return (
                      <>
                        <hr />
                        <h5>Ratings</h5>
                        <RatingsTable
                          rows={[
                            { label: 'Original', ratings: originalRatings || {} },
                            { label: 'Updated', ratings: safeParse(g.ratings) || {} }
                          ]}
                        />
                        <hr />
                        <h5>Changes</h5>
                        {Array.isArray(c) && c.every(item => 
                          typeof item === 'object' && 
                          item.change && 
                          item.why_it_helps && 
                          item.theory &&
                          typeof item.theory === 'object' &&
                          item.theory.tags &&
                          item.theory.explanation
                        ) ? (
                          <ChangesDisplay 
                            changes={c} 
                            ratings={g.ratings} 
                            ratingLabels={ratingLabels} 
                          />
                        ) : Array.isArray(c) && c.every(item => 
                          typeof item === 'object' && 
                          item.change && 
                          item.why_it_helps && 
                          item.theory &&
                          typeof item.theory === 'string'
                        ) ? (
                          <div className="table-responsive">
                            <table className="table table-sm table-bordered">
                              <thead>
                                <tr>
                                  <th style={{width: '25%'}}>Change Made</th>
                                  <th style={{width: '35%'}}>Why It Helps</th>
                                  <th style={{width: '40%'}}>Theory & Explanation</th>
                                </tr>
                              </thead>
                              <tbody>
                                {c.map((item, i) => (
                                  <tr key={i}>
                                    <td className="small">{String(item.change)}</td>
                                    <td className="small">{String(item.why_it_helps)}</td>
                                    <td className="small text-muted">{String(item.theory)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="alert alert-warning">
                            <strong>Unexpected comment format:</strong>
                            <pre className="small mt-2 mb-0">{JSON.stringify(c, null, 2)}</pre>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-primary"
                    onClick={() =>
                      window.dispatchEvent(
                        new CustomEvent('useDraft', {
                          detail: g.scene || '',
                        }),
                      )
                    }
                  >
                    Use as Draft
                  </button>
                  <button className="btn btn-secondary" data-bs-dismiss="modal">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}